"use client";

import Link from "next/link";
import NextImage from '@/components/ui/MediaImage';
import { motion } from "framer-motion";
import {
  gridItemVariants,
  pageVariants,
  slideFromRightVariants,
  staggerContainerVariants,
} from "@/lib/animations";
import BlogRightBar, { type BlogRightBarProps } from "@/components/sections/BlogRightBar";
import type { BlogPostDetailViewModel } from "@/types/blog";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";

interface BlogPostHomeProps {
  post: BlogPostDetailViewModel | null;
  language: string;
  defaultLanguage: string;
  sidebar?: BlogRightBarProps;
  translations?: Record<string, string>;
}

const translate = (
  translations: Record<string, string> | undefined,
  key: string,
  fallback: string
): string => translations?.[key] ?? fallback;

const formatDate = (value?: string, language?: string) => {
  if (!value) {
    return language === "tr" ? "Tarih yok" : "No date";
  }
  try {
    const date = new Date(value);
    return new Intl.DateTimeFormat(language === "tr" ? "tr-TR" : "en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  } catch {
    return value;
  }
};

export default function BlogPostHome({
  post,
  language,
  defaultLanguage,
  sidebar,
  translations,
}: BlogPostHomeProps) {
  if (!post) {
    return (
      <section className="w-9/12 mx-auto px-4 py-16">
        <div className="rounded border border-dashed border-alsero-soft-white bg-white px-8 py-16 text-center text-lg text-alsero-light-gray">
          {translate(translations, 'blog_post_not_found', 'Blog post could not be found.')}
        </div>
      </section>
    );
  }

  const coverHeight = post.cover?.height ?? 450;
  const coverWidth = post.cover?.width ?? 850;

  return (
    <motion.div
      className="w-9/12 mx-auto px-4 py-16"
      initial="hidden"
      animate="visible"
      variants={pageVariants}
    >
      <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 relative">
        <motion.div
          className="col-span-12 lg:col-span-8"
          variants={staggerContainerVariants}
        >
          <motion.article
            initial={{ opacity: 0, y: 60, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="group"
          >
            {post.cover?.src && (
              <motion.div
                className="relative w-full overflow-hidden"
                style={{ height: `${coverHeight}px` }}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.4 }}
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="relative h-full w-full"
                >
                  <NextImage
                    src={post.cover.src}
                    alt={post.cover.alt}
                    className="h-full w-full object-cover"
                    height={coverHeight}
                    width={coverWidth}
                    quality={90}
                    unoptimized={post.cover.src.startsWith('/api/media/proxy/')}
                  />

                  <div className="absolute top-5 left-5 w-[50px] py-2 bg-alsero-first rounded-sm font-semibold text-white text-sm flex justify-center">
                    <span className="w-[28px] text-center">
                      {formatDate(post.date, language)}
                    </span>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"
                />
              </motion.div>
            )}

            <div className="px-3 pb-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="mt-4 mb-3 flex flex-col gap-3 text-sm text-[#020626]"
              >
                <div className="flex flex-wrap items-center gap-4">
                  <span className="flex items-center gap-3">
                    <svg width="14" height="17" viewBox="0 0 14 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.5 9.56812C11.5312 9.56812 14 12.0369 14 15.0681C14 15.6306 13.5312 16.0681 13 16.0681H1C0.4375 16.0681 0 15.6306 0 15.0681C0 12.0369 2.4375 9.56812 5.5 9.56812H8.5ZM1.5 14.5681H12.4688C12.2188 12.5994 10.5312 11.0681 8.5 11.0681H5.5C3.4375 11.0681 1.75 12.5994 1.5 14.5681ZM7 8.06812C4.78125 8.06812 3 6.28687 3 4.06812C3 1.88062 4.78125 0.0681152 7 0.0681152C9.1875 0.0681152 11 1.88062 11 4.06812C11 6.28687 9.1875 8.06812 7 8.06812ZM7 1.56812C5.59375 1.56812 4.5 2.69312 4.5 4.06812C4.5 5.47437 5.59375 6.56812 7 6.56812C8.375 6.56812 9.5 5.47437 9.5 4.06812C9.5 2.69312 8.375 1.56812 7 1.56812Z" fill="#E31E24" />
                    </svg>
                    {post.author}
                  </span>
                  <span className="flex items-center gap-2">
                    <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7.76562 0.019165C11.5117 0.019165 14.5469 3.05432 14.5469 6.80042C14.5469 10.5465 11.5117 13.5817 7.76562 13.5817C4.01953 13.5817 0.984375 10.5465 0.984375 6.80042C0.984375 3.05432 4.01953 0.019165 7.76562 0.019165ZM7.76562 12.2692C10.7734 12.2692 13.2344 9.83557 13.2344 6.80042C13.2344 3.7926 10.7734 1.33167 7.76562 1.33167C4.73047 1.33167 2.29688 3.7926 2.29688 6.80042C2.29688 9.83557 4.73047 12.2692 7.76562 12.2692ZM9.43359 9.42542L7.10938 7.7301C7.02734 7.67542 7 7.56604 7 7.48401V2.97229C7 2.80823 7.13672 2.64417 7.32812 2.64417H8.20312C8.36719 2.64417 8.53125 2.80823 8.53125 2.97229V6.8551L10.3359 8.19495C10.5 8.30432 10.5273 8.49573 10.418 8.65979L9.89844 9.34338C9.78906 9.50745 9.59766 9.53479 9.43359 9.42542Z" fill="#E31E24" />
                    </svg>
                    {post.readTime}
                  </span>
                </div>
                {post.category && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-alsero-first">
                      {translate(translations, 'blog_post_category_label', 'Category:')}
                    </span>
                    <Link href={post.category.link} className="hover:text-alsero-first">
                      {post.category.title} 
                    </Link>
                  </div>
                )}
              </motion.div>
              <motion.h2
                className="text-3xl font-semibold text-[#020626] mb-5 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                {post.title}
              </motion.h2>
              {post.summary && (
                <p className="mb-6 text-lg text-alsero-light-gray leading-8">
                  {post.summary}
                </p>
              )}
              {post.content ? (
                <div
                  className="prose prose-lg max-w-none text-[#020626]"
                  dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(post.content) }}
                />
              ) : (
                <p className="text-lg text-alsero-light-gray leading-8">
                  {translate(translations, 'blog_post_no_content', 'Content for this post is not available yet.')}
                </p>
              )}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-10 flex flex-wrap items-center gap-3">
                  <span className="font-medium text-alsero-first">
                    {translate(translations, 'blog_post_tags_label', 'Tags:')}
                  </span>
                  {post.tags.map((tag, index) => (
                    <Link
                      key={`${tag.link}-${index}`}
                      href={tag.link}
                      className="rounded-full border border-alsero-soft-white px-3 py-1 text-sm text-alsero-dark-gray transition-colors duration-300 hover:border-alsero-first hover:bg-alsero-first hover:text-white"
                    >
                      {tag.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </motion.article>
        </motion.div>
        <motion.div
          className="col-span-12 lg:col-span-4 self-start"
          variants={slideFromRightVariants}
        >
          {sidebar ? (
            <BlogRightBar
              recentPosts={sidebar.recentPosts}
              categories={sidebar.categories}
              tags={sidebar.tags}
              language={sidebar.language}
              defaultLanguage={sidebar.defaultLanguage}
              searchUrl={sidebar.searchUrl}
              translations={sidebar.translations}
            />
          ) : (
            <motion.div
              className="rounded border border-dashed border-alsero-soft-white bg-white px-6 py-8 text-sm text-alsero-light-gray"
              variants={gridItemVariants}
            >
              {translate(translations, 'blog_post_sidebar_error', 'Sidebar data could not be loaded.')}
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}